import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IDP = (process.env.REACT_APP_IDENTITY_PLATFORM_URL || 'https://id.truckwash1.cloud').replace(/\/$/, '');

/**
 * Returns the URL to send the user to at the Identity Platform to start SSO.
 * After successful login, Identity Platform redirects to <vault>/auth/callback
 * with ?access_token=X&refresh_token=Y appended.
 */
export function buildIdentityLoginUrl() {
  const callback = `${window.location.origin}/auth/callback`;
  return `${IDP}/login?redirect=${encodeURIComponent(callback)}`;
}

export function redirectToIdentityLogin() {
  window.location.replace(buildIdentityLoginUrl());
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [paginas, setPaginas] = useState(null); // page-level rights from IDP
  const [hasAccess, setHasAccess] = useState(true);
  const [loading, setLoading] = useState(true);

  // In-memory tokens (NOT localStorage — tokens lost on refresh, user is
  // redirected back to Identity Platform to re-authenticate).
  const accessTokenRef = useRef(null);
  const refreshTokenRef = useRef(null);

  const clearAuth = useCallback(() => {
    accessTokenRef.current = null;
    refreshTokenRef.current = null;
    setUser(null);
    setPaginas(null);
    setHasAccess(true);
  }, []);

  /**
   * Fetch page rights from Identity Platform and set auth state.
   * Returns true on success, false on failure.
   */
  const bootstrapSession = useCallback(async (accessToken) => {
    try {
      // 1. Fetch Vault user record (validates token via IDP too)
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      // 2. Fetch page rights for Vault from Identity Platform
      let rights = { toegang: true, paginas: {} };
      try {
        const rightsRes = await axios.get(`${IDP}/api/user-rechten/vault`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        rights = rightsRes.data || rights;
      } catch (e) {
        // If IDP rights endpoint fails, fail-open: allow access, empty paginas
        // (AppShell treats missing keys as "visible").
        // eslint-disable-next-line no-console
        console.warn('Kon toegangsrechten niet ophalen:', e?.response?.status);
      }
      setUser(meRes.data);
      setPaginas(rights.paginas || {});
      setHasAccess(rights.toegang !== false);
      return true;
    } catch (e) {
      clearAuth();
      return false;
    }
  }, [clearAuth]);

  /** Called by AuthCallback page after extracting tokens from URL. */
  const setTokens = useCallback(async (accessToken, refreshToken) => {
    accessTokenRef.current = accessToken;
    refreshTokenRef.current = refreshToken || null;
    const ok = await bootstrapSession(accessToken);
    return ok;
  }, [bootstrapSession]);

  /**
   * Call Identity Platform /api/auth/refresh. Stores new tokens in memory.
   * Returns new access token, or null on failure.
   */
  const refreshAuth = useCallback(async () => {
    const rt = refreshTokenRef.current;
    if (!rt) return null;
    try {
      const res = await axios.post(`${IDP}/api/auth/refresh`, { refresh_token: rt });
      const { access_token, refresh_token } = res.data || {};
      if (!access_token) throw new Error('No access_token in refresh response');
      accessTokenRef.current = access_token;
      if (refresh_token) refreshTokenRef.current = refresh_token;
      return access_token;
    } catch (e) {
      return null;
    }
  }, []);

  /**
   * Logout: invalidate refresh token on IDP, clear local Vault session cache,
   * clear in-memory tokens, then redirect to IDP login.
   */
  const logout = useCallback(async () => {
    const rt = refreshTokenRef.current;
    const at = accessTokenRef.current;
    // Best-effort: call IDP logout (server-side revoke)
    if (rt) {
      try {
        await axios.post(`${IDP}/api/auth/logout`, { refresh_token: rt });
      } catch (e) { /* ignore network failure */ }
    }
    // Best-effort: clear Vault's validation cache
    if (at) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${at}` },
        });
      } catch (e) { /* ignore */ }
    }
    clearAuth();
    redirectToIdentityLogin();
  }, [clearAuth]);

  /**
   * Returns an axios instance scoped to Vault's API with the current access token
   * attached to every request and a 401 interceptor that hits IDP refresh.
   */
  const authAxios = useCallback(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const token = accessTokenRef.current;
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401 && !error.config._retry) {
          error.config._retry = true;
          const newToken = await refreshAuth();
          if (newToken) {
            error.config.headers.Authorization = `Bearer ${newToken}`;
            return axios(error.config);
          }
          // Refresh failed → send user back to IDP login
          clearAuth();
          redirectToIdentityLogin();
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [refreshAuth, clearAuth]);

  // Initial mount: no tokens in memory → not loading (Login.js will redirect).
  useEffect(() => {
    setLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      paginas,
      hasAccess,
      loading,
      setTokens,
      logout,
      authAxios,
      accessTokenRef,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
